import type { WidgetInstance } from './dashboard.service';
import type { DataQueryColumn } from '../Shell/PandoraSolutionBuilderAgent/modules/DataQueryModel/contracts';
import { EventHandler, HandlerCollection } from '../Utils/mx-handler.collection';
import { OperatorsMap } from '../Shell/PandoraSolutionBuilderAgent/modules/DataQueryModel/column-filters/mx-grid-column-filter-operators-factory';
import type { SearchCondition } from '../Shell/PandoraSolutionBuilderAgent/model/search-query/search-form.contracts';
import { MxObjectPropertyType } from '../Shell/Pandora/model/object';

interface SearchConditionWithGroupId extends SearchCondition {
	groupId?: number;
}

interface InteractiveFilterSearchGroups {
	widgetInstance: WidgetInstance;
	conditions: SearchConditionWithGroupId[];
}

interface ChangeHandlerValue {
	valueChanged: boolean;
	value: InteractiveFilterSearchGroups[];
	stringValueChanged: boolean;
	stringValue: string;
	shortStringValue: string;
}

export type InteractiveFilterChangeHandler = EventHandler<ChangeHandlerValue, void>;
type InteractiveFilterValue = unknown[] | null | undefined;

export interface InteractiveFilterForLayoutDesigner {
	filterValue: InteractiveFilterValue;
	name: string;
	operator: number;
}

export class InteractiveFilter {
	public readonly type: string;
	public readonly isEnum: boolean;
	public readonly isPickup: boolean;
	public readonly referenceTo?: string;
	public referenceFilter?: string;
	public stringValue = '';
	private defaultValue?: InteractiveFilterValue;
	private defaultStringValue = '';

	private filterValue: InteractiveFilterValue;
	private readonly map: Map<WidgetInstance, { columns: DataQueryColumn[]; groupId?: number }> = new Map();

	private readonly changeHandlers = new HandlerCollection<ChangeHandlerValue, void>();
	public operator: number;
	public position?: number;
	public required = false;
	public isMultiPicker = true;

	public trackById = Math.random();
	public shortStringValue = '';

	private readonly regStateGroup = /StateGroup=\d+/i;

	constructor(
		public readonly name: string,
		public title: string,
		column: DataQueryColumn,
		widgetInstance: WidgetInstance,
		public readonly operators: OperatorsMap,
		public readonly subfilters: string = ''
	) {
		this.type = column.AttributeValueType;
		this.isEnum = column.IsEnum;
		this.isPickup = column.IsPickup;
		this.referenceTo = column.ReferenceTo;

		const isIdColumn = column.Expression?.toLowerCase() === 'id';
		if (isIdColumn) {
			this.isPickup = true;
			this.referenceTo = column.EntityClassName;
			this.operator = this.operators.Equals.value;
		} else {
			this.operator = this.getOperatorByColumnType(this.type);
		}

		this.referenceFilter = column.ReferenceFilter;
		this.map.set(widgetInstance, { columns: [column], groupId: undefined });
	}

	public setGroupId(widgetInstance: WidgetInstance, groupId: number): void {
		const mapEntry = this.map.get(widgetInstance);
		if (mapEntry) {
			mapEntry.groupId = groupId;
		}
	}

	private getOperatorByColumnType(type: string) {
		if (this.isEnum || this.isPickup) {
			return this.operators.In.value;
		}

		switch (type) {
			case MxObjectPropertyType.Boolean.name:
				return this.operators.In.value;

			case MxObjectPropertyType.Currency.name:
			case MxObjectPropertyType.Integer.name:
			case MxObjectPropertyType.Date.name:
				return this.operators.Between.value;

			case MxObjectPropertyType.String.name:
				return this.operators.Contains.value;

			default:
				return this.operators.Equals.value;
		}
	}

	public merge(filter: InteractiveFilter) {
		if (typeof filter.defaultValue !== 'undefined' && typeof this.defaultValue === 'undefined') {
			this.setDefaultValue(filter.defaultValue, filter.defaultStringValue);
		}

		if (filter.hasValue() && !this.hasValue()) {
			this.setValue(filter.value, filter.stringValue, filter.shortStringValue);
		}
		this.required = this.required || filter.required;
		this.isMultiPicker = this.isMultiPicker && filter.isMultiPicker;

		if ((this.isEnum || this.isPickup) && (this.regStateGroup.test(filter.referenceFilter ?? '') || this.regStateGroup.test(this.referenceFilter ?? ''))) {
			if (!this.referenceFilter) {
				this.referenceFilter = filter.referenceFilter;
			} else if (filter.referenceFilter) {
				this.referenceFilter = Array.from(new Set([...this.referenceFilter.split(/\s*or\s*/i), ...filter.referenceFilter.split(/\s*or\s*/i)])).join(' OR ');
			}
		}

		filter.map.forEach((mapData, widgetInstance) => {
			const exist = this.map.get(widgetInstance);
			if (!exist) {
				this.map.set(widgetInstance, {
					columns: mapData.columns,
					groupId: mapData.groupId,
				});
			} else {
				mapData.columns.forEach((column) => {
					if (!exist.columns.includes(column)) {
						exist.columns.push(column);
					}
				});
				// Keep existing groupId or use new one if none exists
				if (!exist.groupId && mapData.groupId) {
					exist.groupId = mapData.groupId;
				}
			}
		});
	}

	public isMergeable(filter: InteractiveFilter) {
		if (this.name === filter.name && this.type === filter.type) {
			if (this.isEnum || this.isPickup) {
				return (
					this.referenceTo === filter.referenceTo &&
					(this.referenceFilter === filter.referenceFilter ||
						(this.regStateGroup.test(this.referenceFilter ?? '') && this.regStateGroup.test(filter.referenceFilter ?? '')) ||
						(!this.referenceFilter && this.regStateGroup.test(filter.referenceFilter ?? '')) ||
						(this.regStateGroup.test(this.referenceFilter ?? '') && !filter.referenceFilter))
				);
			} else {
				return true;
			}
		}
		return false;
	}

	get value() {
		return this.filterValue;
	}

	setDefaultValue(value: InteractiveFilterValue, stringValue = '') {
		this.defaultValue = value;
		this.defaultStringValue = stringValue;
		if (!this.hasValue()) {
			this.setValue(value, stringValue);
		}
	}

	resetToDefaultValue() {
		this.setValue(this.defaultValue, this.defaultStringValue, this.defaultStringValue);
	}

	setValue(newValue: InteractiveFilterValue, stringValue: string, shortStringValue?: string) {
		const valueChanged = JSON.stringify(this.filterValue) !== JSON.stringify(newValue);
		const stringValueChanged = this.stringValue !== stringValue;
		if (valueChanged || stringValueChanged) {
			this.filterValue = newValue;
			this.stringValue = stringValue;
			this.shortStringValue = shortStringValue ?? stringValue;
			this.changeHandlers.execute({
				value: this.getSearchConditions(),
				valueChanged,
				stringValueChanged,
				stringValue,
				shortStringValue: this.shortStringValue,
			});
		}
		if (this.defaultValue && this.isDefaultValue() && this.stringValue) {
			this.defaultStringValue = this.stringValue;
		}
	}

	clear() {
		this.setValue(undefined, '');
	}

	onChange(handler: InteractiveFilterChangeHandler) {
		return this.changeHandlers.add(handler);
	}

	hasValue(): this is this & { value: unknown[] } {
		return !(this.value === null || typeof this.value === 'undefined' || (Array.isArray(this.value) && this.value.length === 0));
	}

	isDefaultValue(): boolean {
		return Array.isArray(this.defaultValue) && this.defaultValue.length === this.filterValue?.length && this.defaultValue.every((v, i) => v === this.filterValue?.[i]);
	}

	getSearchConditions(): InteractiveFilterSearchGroups[] {
		const groups: InteractiveFilterSearchGroups[] = [];

		// filters with same logical name are merged across multiple widget instances
		// merge does not reconcile operator differences, it's non deterministic
		// to avoid different operator values for same logical filter name
		// we use a deterministic solution:
		this.map.forEach((mapData, widgetInstance) => {
			const { columns, groupId } = mapData;

			const conditions = this.hasValue()
				? columns.map((column) => {
						const isIdColumn = column.Expression?.toLowerCase() === 'id';
						const operatorForColumn = (() => {
							if (isIdColumn) {
								return this.operators.Equals.value;
							}
							if (column.IsEnum || column.IsPickup) {
								return this.operators.In.value;
							}
							switch (column.AttributeValueType) {
								case MxObjectPropertyType.Boolean.name:
									return this.operators.In.value;
								case MxObjectPropertyType.Currency.name:
								case MxObjectPropertyType.Integer.name:
								case MxObjectPropertyType.Date.name:
									return this.operators.Between.value;
								case MxObjectPropertyType.String.name:
									return this.operators.Contains.value;
								default:
									return this.operators.Equals.value;
							}
						})();

						const condition = {
							Property: column.Name,
							Operator: operatorForColumn,
							Value: this.value as unknown[],
						} as SearchConditionWithGroupId;

						// Add groupId only if it exists for this widget instance
						if (groupId) {
							condition.groupId = groupId;
						}

						return condition;
					})
				: [];

			groups.push({ widgetInstance: widgetInstance, conditions });
		});

		return groups;
	}
}
